import {DateTime} from 'luxon';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DateTimeFormatterBase} from '../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {ContactCardDetailsCustomFieldsContainer} from '../contactCard/details/contactCardDetailsCustomFieldsContainer';
import {ContactCardChildItem} from '../contactCard/details/items/contactCardChildItem';

/*
 * Constant.
 */

const defaultNumberOfCustomFields = 3;

/*
 * Props.
 */

export interface TeammateCardContentProps {
  teammate: TeammateModel;
  timeFormat: TimeFormatsEnum;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div``;

// this style needs to be grid, as we reuse a ContactCard element
// who's parent has a similar css.
const StyledAttributesWrapper = styled.div`
  display: grid;
  row-gap: 10px;
  grid-template-columns: auto 1fr;
  padding: 16px 16px 13px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade70};
  overflow: auto;
  /* 
    Compensates the negative margin-bottom that gets added on the last element to avoid cutting the copy button off. See ContactCardSectionItem.
  */
  padding-bottom: 14px;
`;

const StyledFooterShowMoreDiv = styled.div`
  display: flex;
  margin-left: 2px;
  margin-bottom: 7px;
`;

/*
 * Component.
 */

export const TeammateCardContent: FC<TeammateCardContentProps> = (props) => {
  const {teammate, timeFormat} = props;
  const {customFieldAttributes} = teammate;
  const [showMore, setShowMore] = useState(
    Boolean(customFieldAttributes && customFieldAttributes.length > defaultNumberOfCustomFields),
  );

  const dateTimeFormat = useMemo(
    (): Intl.DateTimeFormatOptions => ({
      timeStyle: 'short',
      ...build12Or24hOptionsForFormat(timeFormat),
    }),
    [timeFormat],
  );

  const hasTimezone = useMemo(() => teammate.timezone !== '', [teammate.timezone]);

  return (
    <StyledContentDiv>
      <StyledAttributesWrapper>
        <ContactCardChildItem
          key={teammate.email}
          text={teammate.email}
          title={<Trans id="teammate-card-email-row">Email</Trans>}
          shouldAllowMultiLineWrapping={false}
        >
          {teammate.email}
        </ContactCardChildItem>
        {hasTimezone && (
          <ContactCardChildItem
            key="teammate-local-time"
            text=""
            title={<Trans id="teammate-card-profile-local-time">Local time</Trans>}
            shouldAllowMultiLineWrapping={false}
          >
            <DateTimeFormatterBase
              value={DateTime.local()}
              format={dateTimeFormat}
              timezone={teammate.timezone}
            />
          </ContactCardChildItem>
        )}
        {teammate.customFieldAttributes && (
          <ContactCardDetailsCustomFieldsContainer
            customAttributes={teammate.customFieldAttributes}
            availableItemsToRender={
              showMore ? defaultNumberOfCustomFields : teammate.customFieldAttributes.length
            }
          />
        )}
      </StyledAttributesWrapper>
      {showMore && (
        <StyledFooterShowMoreDiv>
          <Button variant={ButtonVariantsEnum.LINK} onClick={() => setShowMore(false)}>
            <Trans id="teammate-card-customfields-show-more">Show more</Trans>
          </Button>
        </StyledFooterShowMoreDiv>
      )}
    </StyledContentDiv>
  );
};
